import React, { useCallback, useRef, useState } from 'react';
import { DropdownMenuItem, StudyList } from '@ohif/ui-next';
import { useSystem } from '@ohif/core';
import { deleteStudy } from '../../clients/viewer';
import { describeError } from '../../utils';
import DeleteConfirmDialog from './DeleteConfirmDialog';

interface DeleteActionProps {
  cell: any;
  table: any;
}

function clampPageIfEmptied(table: DeleteActionProps['table']): void {
  const { pageIndex } = table.getState().pagination;
  const isLastPage = pageIndex === table.getPageCount() - 1;
  const isOnlyRowOnPage = table.getRowModel().rows.length === 1;

  if (isOnlyRowOnPage && isLastPage && pageIndex > 0) {
    table.setPageIndex(pageIndex - 1);
  }
}

export default function DeleteAction({ cell, table }: DeleteActionProps) {
  const { extensionManager } = useSystem();
  const menuCellRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const studyRow = cell.row.original;
  const studyInstanceUid = studyRow.studyInstanceUid;

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (isDeleting) {
        return;
      }
      setOpen(next);
      if (!next) {
        setHasError(false);
      }
    },
    [isDeleting]
  );

  const onConfirm = useCallback(async () => {
    setIsDeleting(true);
    setHasError(false);

    try {
      await deleteStudy(studyInstanceUid);
    } catch (error) {
      console.error(`[sycai] delete failed for ${studyInstanceUid} ${describeError(error)}`);
      setIsDeleting(false);
      setHasError(true);
      return;
    }

    clampPageIfEmptied(table);

    const sourceName = extensionManager.activeDataSourceName;
    const configuration = extensionManager.getDataSourceDefinition(sourceName)?.configuration;
    extensionManager.updateDataSourceConfiguration(sourceName, { ...configuration });

    setIsDeleting(false);
    setOpen(false);
  }, [extensionManager, studyInstanceUid, table]);

  return (
    <>
      <StudyList.ActionOverlayCell cell={cell}>
        <StudyList.ActionOverlayCell.Overlay>
          <div
            ref={menuCellRef}
            onClick={event => event.stopPropagation()}
          >
            <StudyList.WorkflowMenu
              studyRow={studyRow}
              extraItems={
                <DropdownMenuItem
                  data-cy="sycai-worklist-delete"
                  onSelect={() => setOpen(true)}
                >
                  Delete
                </DropdownMenuItem>
              }
            />
          </div>
        </StudyList.ActionOverlayCell.Overlay>
      </StudyList.ActionOverlayCell>
      <DeleteConfirmDialog
        open={open}
        onOpenChange={onOpenChange}
        hasError={hasError}
        isDeleting={isDeleting}
        onConfirm={onConfirm}
        onCloseAutoFocus={event => {
          event.preventDefault();
          menuCellRef.current?.querySelector('button')?.focus();
        }}
      />
    </>
  );
}
